import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bounce, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./BarCodeScanner.scss";
import { API } from "../../constants/api";
import { callApiWithAuth, Logout } from "../../features/request";
import { Product } from "../../interfaces/IProduct";
import { useProducts } from "../../hooks/useProducts";
import { useScanner } from "../../hooks/useScanner";
import { formatDate } from "../../features/utils";
import ProductList from "../ProductList/Productlist";
import BurgerMenu from "../BurgerMenu/BurgerMenu";

export default function BarcodeScanner() {
  const nav = useNavigate();
  const { products, refs, highlighted, add, del, inc, dec, clearAll } =
    useProducts();

  const { isScanning, startScan, stopScan, regionRef } =
    useScanner(handleDecoded);

  const [starting, setStarting] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);

  const safeStopScan = async () => {
    if (isScanning) {
      await stopScan();
    }
  };
  const toggleMenu = async () => {
    if (!menuOpen) {
      await safeStopScan();
    }
    setMenuOpen((o) => !o);
  };

  const warmed = useRef(false);

  async function warmUpCamera() {
    if (warmed.current) return;
    await navigator.mediaDevices.enumerateDevices();
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      s.getTracks().forEach((t) => t.stop());
    } catch (_) {
      toast.error("Нужен доступ к камере");
      return;
    }
    warmed.current = true;
  }

  useEffect(() => {
    const h = async () => {
      if (document.visibilityState === "visible") {
        await warmUpCamera();
        window.removeEventListener("visibilitychange", h);
      }
    };
    window.addEventListener("visibilitychange", h);
    return () => window.removeEventListener("visibilitychange", h);
  }, []);

  const clearProducts = () => {
    clearAll();
    toast.success("Список очищен");
    setMenuOpen(false);
  };

  const clearLocalStorage = async () => {
    await doLogout();
    setMenuOpen(false);
  };

  async function handleDecoded(barcode: string) {
    try {
      const { product } = await callApiWithAuth<{ product: Product }>(
        `${API}/products/get-by-barcode/${barcode}`,
        "getdata"
      );
      if (!product) {
        toast.warning("Товар не найден", { theme: "dark" });
        return;
      }
      await stopScan();
      add(product);
    } catch (err) {
      toast.error("Ошибка сервера, попробуйте ещё");
      console.error(err);
    }
  }

  const submitting = useRef(false);
  async function submit() {
    if (submitting.current || !products.length) return;
    await safeStopScan();
    submitting.current = true;
    try {
      await callApiWithAuth(
        `${API}/order`,
        "make",
        {
          data: {
            number: null,
            items: products,
            datetime: formatDate(new Date()),
          },
        },
        true
      );
      toast.success("Отправлено в продажи");
      clearAll();
    } catch (e) {
    } finally {
      submitting.current = false;
    }
  }

  async function doLogout() {
    const access = localStorage.getItem("access");
    const csrf = localStorage.getItem("csrf_token");
    if (access && csrf) await Logout(access, csrf);
    nav("/login");
  }

  const handleStartClick = async () => {
    if (starting || isScanning) return;
    setStarting(true);
    try {
      await warmUpCamera();
      if (!warmed.current) return;
      await new Promise((r) => setTimeout(r, 300));
      await startScan();
    } catch (e) {
      console.error(e);
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <div className="barcode-page">
        <header className="header">
          <h1>DDW QR</h1>
          <BurgerMenu
            isOpen={menuOpen}
            onToggle={toggleMenu}
            onClearProducts={clearProducts}
            onLogout={clearLocalStorage}
          />
        </header>

        {isScanning && (
          <div id="qr-region" ref={regionRef} className="qr-box" />
        )}

        <ProductList
          products={products}
          refs={refs}
          highlighted={highlighted}
          onDelete={del}
          onInc={inc}
          onDec={dec}
        />

        <div className="fab-container">
          {isScanning ? (
            <button className="fab stop" onClick={stopScan}>
              ⏸
            </button>
          ) : (
            <button
              className={`fab start ${starting ? "is-loading" : ""}`}
              onClick={handleStartClick}
              disabled={starting || isScanning}
              aria-busy={starting}
            >
              {starting ? <span className="spinner" /> : "▶"}
            </button>
          )}
        </div>

        <button
          className="submit-button"
          onClick={submit}
          disabled={!products.length}
        >
          Отправить
        </button>
      </div>
      <ToastContainer
        position="top-center"
        theme="dark"
        autoClose={2000}
        transition={Bounce}
      />
    </>
  );
}
